import * as React from "react";
import { Header } from "@gc/global-components";
import { LocalizedComponentProps } from "@shared-ui/localization-context";
import { AnalyticsStore, Controller, logger, PageStore } from "bernie-core";
import { Logger } from "bernie-logger";
import { ViewMedium, Viewport, ViewSmall } from "bernie-view";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";
import { FlexFooter } from "components/footer/FlexFooter";
import { TracingInfo } from "components/footer/TracingInfo";
import { MyList } from "components/header/MyList";
import RedirectBanner from "components/redirectBanner/RedirectBannerWrapper";
import { AdScripts } from "components/utility/Ads/AdScripts";
import { doGlassbox } from "components/utility/analytics/FlexAnalyticsUtils";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { Notification } from "components/utility/Notification/Notification";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { MISSING_CONTEXT_AND_FLEX_VIEWMODEL } from "config/systemEvents";
import { inject } from "mobx-react";
import { QualtricsScript } from "src/components/utility/Qualtrics/QualtricsScript";
import { AdvertisingStore } from "stores/AdvertisingStore";
import { CompositionStore } from "stores/CompositionStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { ExperimentContext, ExtendedContextStore, TrackingContext } from "typings/flexFramework/FlexDefinitions";
import { AlterLocalizationProvider } from "bernie-alter";

interface LandingPageProps extends LocalizedComponentProps {
  advertising?: AdvertisingStore;
  analytics?: AnalyticsStore;
  flexViewModel?: FlexViewModelStore;
  context?: ExtendedContextStore;
  compositionStore?: CompositionStore;
  page?: PageStore;
  route?: Controller;
}

interface LandingPageStates {
  redirectBanner: React.ReactElement | null;
}

@logger("LandingPage")
@inject("advertising", "analytics", "flexViewModel", "context", "compositionStore", "page")
export default class LandingPage extends React.Component<LandingPageProps & { logger?: Logger }, LandingPageStates> {
  public componentDidMount() {
    const { compositionStore, context, analytics } = this.props;
    document.title = compositionStore ? compositionStore.composition.title : "";

    // Record user's interactions with the page if enabled
    if (this.shouldPerformGlassboxAnalytics(context?.experimentContext)) {
      doGlassbox();
    }

    analytics?.markPageUsable();
  }

  private shouldPerformGlassboxAnalytics(context?: ExperimentContext): boolean {
    if (!context) {
      return false;
    }

    const forced = getQueryParamValue("forceGlass") === "true";
    const isStorefrontExperimentEnabled = context.experiments["Flex_Blossom_Glassbox_Throttle"] === 1;
    const isHTGGlassboxEnabled = context.experiments["Flex_HTG_Blossom_Glass_Throttle"] === 1;

    return isStorefrontExperimentEnabled || isHTGGlassboxEnabled || forced;
  }

  // Prevent the page from being rendered twice
  public shouldComponentUpdate(nextProps: any, nextState: any) {
    return false;
  }

  private addDxPageProps = (): { itemScope: boolean; itemType: string } | null => {
    const { context } = this.props;
    const pageType = context && context.searchContext && context.searchContext.pageType;

    if (pageType !== "Destination-Discovery") {
      return null;
    }

    return {
      itemScope: true,
      itemType: "http://schema.org/LocalBusiness",
    };
  };

  public render() {
    if (!this.props.context || !this.props.flexViewModel || !this.props.compositionStore) {
      this.props.logger!.logEvent(
        MISSING_CONTEXT_AND_FLEX_VIEWMODEL,
        `isMissingContext=${!this.props.context} isMissingFlexViewModel=${!this.props
          .flexViewModel} isMissingCompositionStore=${!this.props.compositionStore}`
      );

      return null;
    }

    const extendedContext: ExtendedContextStore = this.props.context;
    const flexViewModel: FlexViewModelStore = this.props.flexViewModel;
    const compositionStore = this.props.compositionStore;
    const trackingContext: TrackingContext = flexViewModel && flexViewModel.trackingContext;

    if (this.props.advertising && extendedContext.queryParams) {
      this.props.advertising.overrideWithQueryParams(extendedContext.queryParams);
    }

    return (
      <AlterLocalizationProvider>
        <RedirectBanner context={extendedContext} />

        <div className="uitk-grid">
          <Header showLobLinks={true}>
            <MyList />
          </Header>
          <div
            className={`uitk-cell ${extendedContext.searchContext && extendedContext.searchContext.pageType}`}
            {...this.addDxPageProps()}
          >
            <BlossomComponents
              flexComponents={compositionStore.composition.page.regionList}
              context={extendedContext}
            />
            <Viewport>
              <ViewSmall>
                <FlexFooter footer={flexViewModel.footer} context={extendedContext} isMobile={true} />
              </ViewSmall>
              <ViewMedium>
                <FlexFooter footer={flexViewModel.footer} context={extendedContext} isMobile={false} />
              </ViewMedium>
            </Viewport>
            <TracingInfo extendedContext={extendedContext} />
          </div>
          <Notification />
        </div>
        <AdScripts pageName={trackingContext.pageName} context={extendedContext} />
        <SchemaOrgScript structuredData={flexViewModel.structuredData} />
        <QualtricsScript context={extendedContext} pageName={trackingContext.pageName} />
      </AlterLocalizationProvider>
    );
  }
}

export { LandingPage };
